import { 
    DynamoNode, NodeTag, ElementNode, PageNode, TableNode,
    RowNode, CellNode, DateNode, CheckboxNode, RadioButtonElementNode,
    DropdownGroupNode, DropdownElementNode, RadioButtonGroupNode, 
    TextBoxNode, SectionNode, TemplateNodeVisitor 
} from "dynamo-models";
import { observable } from "mobx";

export enum ToolboxGroupType {
    Layout,
    Selection,
    Fields,
    Custom,
}

export class ToolboxNode extends DynamoNode {
    constructor(childBuilder: () => ElementNode, isSingleUse: boolean, doRemove?: () => void, label?: string) {
        super();
        this.childBuilder = childBuilder;
        this.child = childBuilder();
        this.isSingleUse = isSingleUse;
        this.doRemove = doRemove !== undefined ? doRemove : () => {};
        this.label = label;
    }

    private isSingleUse: boolean;
    private doRemove: () => void;
    private childBuilder: () => ElementNode;

    @observable public child?: ElementNode;

    public label?: string;

    public tag: NodeTag = NodeTag.Invalid;

    public validateSubmission(shouldShortCircuit: boolean): boolean {
        return false;
    }

    public accept(visitor: TemplateNodeVisitor) {
        if (this.child === undefined) {
            return;
        }

        this.child.accept(visitor);
    }

    public getChildren(): DynamoNode[] {
        if (this.child === undefined) {
            return []
        }

        return [this.child]; 
    }

    public tryAddChild(child: DynamoNode, index?: number | undefined): boolean {
        return false;
    }

    public tryRemoveChild(child: DynamoNode): boolean {
        if (this.child !== child) {
            return false;
        }

        if (this.isSingleUse) {
            this.child = undefined;
            this.doRemove()
        }

        this.child = this.childBuilder();
        return true;
    }
}

export class ToolboxContainerNode extends DynamoNode {
    private children: ToolboxNode[] = [];

    public tag: NodeTag = NodeTag.Invalid;
    
    public validateSubmission(shouldShortCircuit: boolean): boolean {
        return false;
    }

    public accept(visitor: TemplateNodeVisitor) {
        this.children.map(v => v.accept(visitor));
    }

    public getChildren(): DynamoNode[] {
        return this.children;
    }

    public tryAddChild(child: DynamoNode, index?: number | undefined): boolean {
        let elementNodeMaybe = child as ElementNode;
        let toolboxNodeMaybe = child as ToolboxNode;

        if (toolboxNodeMaybe.tag !== NodeTag.Invalid) {
            toolboxNodeMaybe = new ToolboxNode(() => elementNodeMaybe, true, () => this.tryRemoveChild(toolboxNodeMaybe));
        }

        if (index !== undefined) {
            this.children = [...this.children.slice(0, index), toolboxNodeMaybe, ...this.children.slice(index)];
        } else {
            this.children = [...this.children, toolboxNodeMaybe];
        }

        return true;
    }

    public addTemplateChild(childBuilder: () => ElementNode, index?: number, label?: string) {
        let toolboxNode = new ToolboxNode(childBuilder, false, undefined, label);

        this.tryAddChild(toolboxNode, index);
    }

    public addSingleUseChild(child: DynamoNode, index?: number) {
        this.tryAddChild(child, index);
    }

    public tryRemoveChild(child: DynamoNode): boolean {
        let toolboxChildren = this.children as ToolboxNode[];

        for (let toolboxNode of toolboxChildren) {
            if (toolboxNode === child) {
                const origLength = this.children.length;
                this.children = this.children.filter((v) => v.nodeId !== child.nodeId);

                return this.children.length !== origLength;
            }

            if (toolboxNode.child === child) {
                return toolboxNode.tryRemoveChild(child);
            }
        }

        return false;
    }
}

export class ToolboxLayoutNode extends PageNode {
    constructor(groupType: ToolboxGroupType, label?: string, genChildren?: () => ElementNode[]) {
        super();
        this.groupType = groupType;
        this.genChildren = genChildren;
        this.setupChildren();
        this.text = label !== undefined ? label : this.getLabel(groupType);
    }

    private genChildren?: () => ElementNode[];

    private getLabel(type: ToolboxGroupType): string {
        switch (type) {
            case ToolboxGroupType.Fields:
                return "Form Fields";

            case ToolboxGroupType.Layout:
                return "Layout";

            case ToolboxGroupType.Selection:
                return "Selection";

            default:
                return "Group";
        }
    }

    private readonly groupType: ToolboxGroupType;

    private setupChildren() {
        switch (this.groupType) {
            case ToolboxGroupType.Fields:
                this.children = [new TextBoxNode(), new DateNode()];
                break;

            case ToolboxGroupType.Layout:
                this.children = [new SectionNode(), new TableNode(), new RowNode(), new CellNode()];
                break;

            case ToolboxGroupType.Selection:
                this.children = [
                    new RadioButtonGroupNode(), 
                    new RadioButtonElementNode(),
                    new DropdownGroupNode(),
                    new DropdownElementNode(),
                    new CheckboxNode()
                ];
                break;

            default:
                if (this.genChildren !== undefined) {
                    this.children = this.genChildren();
                }
                else {
                    this.children = [];
                }
                break;
        }
    }

    public tryAddChild(child: DynamoNode, index?: number | undefined): boolean {
        return false;
    }

    public tryRemoveChild(child: DynamoNode): boolean {
        if (child.tag !== NodeTag.Element) {
            return false;
        }

        let elemNode = child as ElementNode;

        if (this.children.includes(elemNode)) {
            this.setupChildren();
            return true;
        }

        return false;
    }
}